/**
 * Jurisdiction Hooks
 *
 * React Query hooks for fetching jurisdiction data from Supabase
 * with state subscription access control.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useStateSubscription } from '@/contexts/StateSubscriptionContext';
import type { JurisdictionDetail, StateWithCount, CountyBasic } from '@/types/jurisdiction';

interface UseJurisdictionsOptions {
  stateCode?: string;
  stateCodes?: string[];
  countyId?: number;
  searchTerm?: string;
}

/**
 * Fetch jurisdictions with optional filtering
 */
export function useJurisdictions(options?: UseJurisdictionsOptions) {
  const { subscribedStates, isStateSubscribed } = useStateSubscription();

  return useQuery({
    queryKey: ['jurisdictions', options?.stateCode, options?.stateCodes, options?.countyId, options?.searchTerm, subscribedStates],
    queryFn: async (): Promise<JurisdictionDetail[]> => {
      if (!supabase) return [];

      // If filtering by single state code, first get the state ID
      let stateId: number | null = null;
      if (options?.stateCode) {
        const { data: stateData } = await supabase
          .from('states')
          .select('id')
          .eq('code', options.stateCode.toUpperCase())
          .single();
        stateId = stateData?.id || null;
      }

      // If filtering by multiple state codes, get all state IDs
      let stateIds: number[] = [];
      if (options?.stateCodes && options.stateCodes.length > 0) {
        const { data: statesData } = await supabase
          .from('states')
          .select('id')
          .in('code', options.stateCodes.map((c) => c.toUpperCase()));
        stateIds = (statesData || []).map((s: any) => s.id);
      }

      let query = supabase
        .from('jurisdictions')
        .select(`
          *,
          states(id, code, name),
          counties(id, name),
          cities(id, name)
        `);

      // Filter by single state ID if specified
      if (stateId) {
        query = query.eq('state_id', stateId);
      }
      // Or filter by multiple state IDs
      else if (stateIds.length > 0) {
        query = query.in('state_id', stateIds);
      }

      // Filter by county if provided
      if (options?.countyId) {
        query = query.eq('county_id', options.countyId);
      }

      // Search by name
      if (options?.searchTerm && options.searchTerm.trim()) {
        query = query.ilike('ahj_name', `%${options.searchTerm.trim()}%`);
      }

      const { data, error } = await query.order('ahj_name');

      if (error) throw error;

      // Transform data to include state info and access status
      return (data || []).map((j: any) => ({
        ...j,
        _stateCode: j.states?.code,
        _stateName: j.states?.name,
        _countyName: j.counties?.name,
        _cityName: j.cities?.name,
        _isAccessible: isStateSubscribed(j.states?.code || ''),
      })) as JurisdictionDetail[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch a single jurisdiction with full details
 */
export function useJurisdictionDetail(jurisdictionId: number | null) {
  const { isStateSubscribed } = useStateSubscription();

  return useQuery({
    queryKey: ['jurisdiction', jurisdictionId],
    queryFn: async (): Promise<JurisdictionDetail | null> => {
      if (!supabase || !jurisdictionId) return null;

      const { data, error } = await supabase
        .from('jurisdictions')
        .select(`
          *,
          states!inner(id, code, name),
          counties(id, name),
          cities(id, name)
        `)
        .eq('id', jurisdictionId)
        .single();

      if (error) throw error;
      if (!data) return null;

      const stateCode = data.states?.code;
      const hasAccess = isStateSubscribed(stateCode || '');

      // If user doesn't have access, mask sensitive data
      // Note: department_contacts, notice_of_commencement, trade_specific_forms,
      // owner_builder_rules, and commercial_requirements are NOT masked as they
      // provide useful quick reference info without revealing premium details
      if (!hasAccess) {
        return {
          ...data,
          _stateCode: stateCode,
          _stateName: data.states?.name,
          _countyName: data.counties?.name,
          _cityName: data.cities?.name,
          _isAccessible: false,
          _requiresSubscription: true,
          // Mask premium detailed data only
          fee_schedule: null,
          processing_timelines: null,
          required_documents: null,
          inspection_requirements: null,
          penalties: null,
          permit_exemptions: null,
          review_agencies: null,
        } as JurisdictionDetail;
      }

      return {
        ...data,
        _stateCode: stateCode,
        _stateName: data.states?.name,
        _countyName: data.counties?.name,
        _cityName: data.cities?.name,
        _isAccessible: true,
      } as JurisdictionDetail;
    },
    enabled: !!jurisdictionId,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Fetch all states with their jurisdiction counts
 */
export function useAvailableStates() {
  return useQuery({
    queryKey: ['available-states'],
    queryFn: async (): Promise<StateWithCount[]> => {
      if (!supabase) return [];

      // Get states
      const { data: states, error: statesError } = await supabase
        .from('states')
        .select('id, code, name, region')
        .order('name');

      if (statesError) throw statesError;

      // Get jurisdiction counts per state
      const { data: counts, error: countsError } = await supabase
        .from('jurisdictions')
        .select('state_id')
        .then(({ data }) => {
          // Count jurisdictions per state
          const countMap: Record<number, number> = {};
          (data || []).forEach((j: any) => {
            countMap[j.state_id] = (countMap[j.state_id] || 0) + 1;
          });
          return { data: countMap, error: null };
        });

      if (countsError) throw countsError;

      return (states || []).map((s: any) => ({
        id: s.id,
        code: s.code,
        name: s.name,
        region: s.region,
        jurisdictionCount: counts?.[s.id] || 0,
      }));
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Fetch counties for a specific state - only counties that have jurisdictions
 */
export function useCountiesByState(stateId: number | null) {
  return useQuery({
    queryKey: ['counties-with-jurisdictions', stateId],
    queryFn: async (): Promise<CountyBasic[]> => {
      if (!supabase || !stateId) return [];

      // Only return counties that have at least one jurisdiction
      const { data, error } = await supabase
        .from('counties')
        .select(`
          id,
          name,
          fips_code,
          jurisdictions!inner(id)
        `)
        .eq('state_id', stateId)
        .order('name');

      if (error) throw error;

      // Map to CountyBasic (removing the jurisdictions array)
      return (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        fips_code: c.fips_code,
      })) as CountyBasic[];
    },
    enabled: !!stateId,
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Get state ID by state code
 */
export function useStateByCode(stateCode: string | null) {
  return useQuery({
    queryKey: ['state', stateCode],
    queryFn: async () => {
      if (!supabase || !stateCode) return null;

      const { data, error } = await supabase
        .from('states')
        .select('id, code, name, region')
        .eq('code', stateCode.toUpperCase())
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!stateCode,
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * County with state info for coverage display
 */
export interface CountyWithState {
  id: number;
  name: string;
  fips_code: string | null;
  population: number | null;
  state_id: number;
  stateCode: string;
  stateName: string;
}

/**
 * Fetch all counties with state info for coverage map
 */
export function useAllCounties(options?: { stateCode?: string; searchTerm?: string }) {
  return useQuery({
    queryKey: ['all-counties', options?.stateCode, options?.searchTerm],
    queryFn: async (): Promise<CountyWithState[]> => {
      if (!supabase) return [];

      // If filtering by state code, first get the state ID
      let stateId: number | null = null;
      if (options?.stateCode) {
        const { data: stateData } = await supabase
          .from('states')
          .select('id')
          .eq('code', options.stateCode.toUpperCase())
          .single();
        stateId = stateData?.id || null;
      }

      let query = supabase
        .from('counties')
        .select(`
          id,
          name,
          fips_code,
          population,
          state_id,
          states(code, name)
        `);

      // Filter by state ID if specified
      if (stateId) {
        query = query.eq('state_id', stateId);
      }

      // Search by name
      if (options?.searchTerm && options.searchTerm.trim()) {
        query = query.ilike('name', `%${options.searchTerm.trim()}%`);
      }

      const { data, error } = await query.order('name');

      if (error) throw error;

      // Transform to include state info
      return (data || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        fips_code: c.fips_code,
        population: c.population,
        state_id: c.state_id,
        stateCode: c.states?.code || 'FL',
        stateName: c.states?.name || 'Florida',
      }));
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

/**
 * Jurisdiction with county info for coverage display
 */
export interface JurisdictionForCoverage {
  id: number;
  ahj_name: string;
  ahj_short_code: string | null;
  scope_level: string;
  county_id: number | null;
  countyName: string | null;
  city_id: number | null;
  cityName: string | null;
  stateCode: string;
  stateName: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  portal_url: string | null;
}

/**
 * Fetch all jurisdictions for coverage page
 */
export function useAllJurisdictions(options?: { stateCode?: string; countyId?: number }) {
  return useQuery({
    queryKey: ['all-jurisdictions', options?.stateCode, options?.countyId],
    queryFn: async (): Promise<JurisdictionForCoverage[]> => {
      if (!supabase) return [];

      // If filtering by state code, first get the state ID
      let stateId: number | null = null;
      if (options?.stateCode) {
        const { data: stateData } = await supabase
          .from('states')
          .select('id')
          .eq('code', options.stateCode.toUpperCase())
          .single();
        stateId = stateData?.id || null;
      }

      let query = supabase
        .from('jurisdictions')
        .select(`
          id,
          ahj_name,
          ahj_short_code,
          scope_level,
          county_id,
          city_id,
          phone,
          email,
          website,
          portal_url,
          states(code, name),
          counties(name),
          cities(name)
        `);

      // Filter by state ID if specified
      if (stateId) {
        query = query.eq('state_id', stateId);
      }

      // Filter by county if specified
      if (options?.countyId) {
        query = query.eq('county_id', options.countyId);
      }

      const { data, error } = await query.order('ahj_name');

      if (error) throw error;

      // Transform to include related info
      return (data || []).map((j: any) => ({
        id: j.id,
        ahj_name: j.ahj_name,
        ahj_short_code: j.ahj_short_code,
        scope_level: j.scope_level,
        county_id: j.county_id,
        countyName: j.counties?.name || null,
        city_id: j.city_id,
        cityName: j.cities?.name || null,
        stateCode: j.states?.code || 'FL',
        stateName: j.states?.name || 'Florida',
        phone: j.phone,
        email: j.email,
        website: j.website,
        portal_url: j.portal_url,
      }));
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}
